import React from "react";
import * as styles from "@styles/DocumentCreation.css";
import type { StateUpdate } from "@ps-bridge/capabilitiesTypes";
import type { DocumentCreationViewProps } from "@ps-bridge/contracts/documentCreation";
import type { DocumentMetadata } from "@ps-bridge/models/core/dataContracts";
import type { DocumentID } from "@ps-bridge/models/brands/documentID";
import type { Ok } from "@ps-bridge/ok";
import * as IO from "@ps-bridge/interop/io";
import { FileText } from "lucide-react";

type DocumentCreationFormContractProps = Pick<DocumentCreationViewProps, never>;

type DocumentCreationFormViewProps = DocumentCreationFormContractProps & {
  titleValue: string;
  styleValue: string;
  outlineValue: string;
  onTitleChange: StateUpdate<string>;
  onStyleChange: StateUpdate<string>;
  onOutlineChange: StateUpdate<string>;
  availableDocuments: DocumentMetadata[];
  selectedSourceDocumentIds: DocumentID[];
  onToggleSourceDocument: (documentId: DocumentID) => Ok;
  onUploadSourceDocument: (file: File) => Ok;
  isSourceDocumentUploading: boolean;
  sourceDocumentUploadError: string;
};

const DocumentCreationFormView: React.FC<DocumentCreationFormViewProps> = ({
  titleValue,
  styleValue,
  outlineValue,
  onTitleChange,
  onStyleChange,
  onOutlineChange,
  availableDocuments,
  selectedSourceDocumentIds,
  onToggleSourceDocument,
  onUploadSourceDocument,
  isSourceDocumentUploading,
  sourceDocumentUploadError,
}) => {
  const uploadInputRef = React.useRef<HTMLInputElement>({ click: () => undefined } as HTMLInputElement);

  return (
    <div className={styles.formStack}>
    <div>
      <p className={styles.sectionTitle}>Document name</p>
      <p className={styles.sectionHint}>Give it a working title you can refine later.</p>
      <input
        className={styles.input}
        data-testid="document-creation-title"
        placeholder="e.g., Vendor onboarding memo"
        value={titleValue}
        onChange={(event) => IO.run(onTitleChange(event.target.value))}
      />
    </div>
    <div>
      <p className={styles.sectionTitle}>Intentions & tone</p>
      <p className={styles.sectionHint}>
        Describe the audience, point of view, and any stylistic constraints.
      </p>
      <textarea
        className={styles.multiline}
        data-testid="document-creation-style"
        placeholder="Help me persuade new clients with confident, concise language."
        value={styleValue}
        onChange={(event) => IO.run(onStyleChange(event.target.value))}
      />
    </div>
    <div>
      <p className={styles.sectionTitle}>Planning outline</p>
      <p className={styles.sectionHint}>
        One section title per line. No bullets or numbering needed.
      </p>
      <textarea
        className={styles.multiline}
        data-testid="document-creation-outline"
        placeholder="Executive summary\nCurrent landscape\nRecommended approach"
        value={outlineValue}
        onChange={(event) => IO.run(onOutlineChange(event.target.value))}
      />
    </div>
    <div>
      <div className={styles.sourceHeader}>
        <div>
          <p className={styles.sectionTitle}>Source documents</p>
          <p className={styles.sectionHint}>Select up to two stored documents.</p>
        </div>
        <button
          type="button"
          className={styles.sourceUploadButton}
          data-testid="document-creation-source-upload-button"
          disabled={isSourceDocumentUploading}
          onClick={() => uploadInputRef.current.click()}
        >
          <FileText size={16} aria-hidden="true" />
          <span>{isSourceDocumentUploading ? "Uploading" : "Upload"}</span>
        </button>
        <input
          ref={uploadInputRef}
          className={styles.hiddenFileInput}
          data-testid="document-creation-source-upload-input"
          type="file"
          accept=".md,.docx,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              onUploadSourceDocument(file);
            }
            event.target.value = "";
          }}
        />
      </div>
      <p
        className={styles.sourceUploadError}
        data-testid="document-creation-source-upload-error"
        hidden={sourceDocumentUploadError === ""}
      >
        {sourceDocumentUploadError}
      </p>
      <div className={styles.sourceSelector} data-testid="document-creation-source-selector">
        {availableDocuments.map((document) => {
          const selectedIndex = selectedSourceDocumentIds.indexOf(document.id);
          const isSelected = selectedIndex >= 0;
          const isDisabled = !isSelected && selectedSourceDocumentIds.length >= 2;
          return (
            <button
              key={document.id}
              type="button"
              className={styles.sourceOption}
              data-testid={`document-creation-source-${document.id}`}
              data-selected={isSelected ? "true" : "false"}
              aria-pressed={isSelected}
              disabled={isDisabled}
              onClick={() => onToggleSourceDocument(document.id)}
            >
              <span className={styles.sourceOptionTitle}>{document.name}</span>
              <span className={styles.sourceOptionMeta}>
                {isSelected ? `Selected ${selectedIndex + 1}` : "Available"}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  </div>
  );
};

export default DocumentCreationFormView;
